import React from 'react';
import {
  Box,
  Chip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';

const AssociationTable = ({ associations }) => {
  const navigate = useNavigate();

  const disorderIdMap = {
    'ASC': 'asc', 'SCHEMA': 'schema', 'BipEx': 'bipex', 'Epi25': 'epi25',
    'MDD': 'mdd', 'NDD': 'ndd', 'ADHD': 'adhd', 'Cognition': 'cognition', 'OCD': 'ocd',
  };

  const disordersWithSubtypes = ['bipex', 'epi25', 'cognition'];

  const isFilterableSubtype = (disorder, subtype) => {
    if (!subtype) return false;
    if (['All', 'PTV', 'Missense', 'De novo'].includes(subtype)) return false;
    const disorderId = disorderIdMap[disorder];
    return disorderId && disordersWithSubtypes.includes(disorderId);
  };

  const getSignificanceColor = (pval) => {
    if (!pval) return 'default';
    if (pval < 5e-8) return 'error';
    if (pval < 1e-6) return 'warning';
    if (pval < 0.05) return 'info';
    return 'default';
  };

  const formatPValue = (pval) => {
    if (pval === null || pval === undefined) return '';
    if (pval < 1e-10) return pval.toExponential(2);
    if (pval < 0.001) return pval.toExponential(2);
    return pval.toFixed(4);
  };

  const formatOR = (or_val, ci) => {
    if (or_val === null || or_val === undefined) return 'N/A';
    if (ci && ci[0] !== null && ci[1] !== null) {
      return `${or_val.toFixed(2)} (${ci[0].toFixed(2)}-${ci[1].toFixed(2)})`;
    }
    return or_val.toFixed(2);
  };

  const PvalChip = ({ value }) => {
    if (value === null || value === undefined) return '';
    return <Chip label={formatPValue(value)} color={getSignificanceColor(value)} size="small" />;
  };

  // Build rows (same logic as before)
  const rows = [];
  let rowId = 0;

  associations.asc?.forEach((asc) => {
    rows.push({ id: rowId++, disorder: 'ASC', subtype: asc.group || 'All',
      ptv_pval: null, ptv_qval: asc.qval, ptv_or: null, ptv_ci: null,
      missense_pval: null, missense_qval: null, missense_or: null, missense_ci: null,
      case_ptv: asc.case_dn_ptv, control_ptv: asc.control_dn_ptv });
  });

  associations.schema?.forEach((schema) => {
    rows.push({ id: rowId++, disorder: 'SCHEMA', subtype: schema.group || 'meta',
      ptv_pval: schema.p_meta, ptv_qval: schema.q_meta,
      ptv_or: schema.or_ptv, ptv_ci: schema.or_ptv_ci,
      missense_pval: schema.p_meta, missense_qval: schema.q_meta,
      missense_or: schema.or_class1, missense_ci: schema.or_class1_ci,
      case_ptv: schema.case_ptv, control_ptv: schema.control_ptv });
  });

  associations.bipex?.forEach((bipex) => {
    rows.push({ id: rowId++, disorder: 'BipEx', subtype: bipex.group || '',
      ptv_pval: bipex.ptv_pval, ptv_qval: null,
      ptv_or: bipex.ptv_or, ptv_ci: null,
      missense_pval: bipex.damaging_missense_pval, missense_qval: null,
      missense_or: bipex.damaging_missense_or, missense_ci: null,
      case_ptv: bipex.ptv_case_count, control_ptv: bipex.ptv_control_count });
  });

  associations.epi25?.forEach((epi25) => {
    rows.push({ id: rowId++, disorder: 'Epi25', subtype: epi25.group || '',
      ptv_pval: epi25.ptv_pval, ptv_qval: null,
      ptv_or: epi25.ptv_or, ptv_ci: null,
      missense_pval: epi25.damaging_missense_pval, missense_qval: null,
      missense_or: epi25.damaging_missense_or, missense_ci: null,
      case_ptv: epi25.ptv_case_count, control_ptv: epi25.ptv_control_count });
  });

  associations.mdd?.forEach((mdd) => {
    if (mdd.variant_type === 'ptv') {
      rows.push({ id: rowId++, disorder: 'MDD', subtype: 'PTV',
        ptv_pval: mdd.pval, ptv_qval: null, ptv_or: mdd.or, ptv_ci: mdd.or_ci,
        missense_pval: null, missense_qval: null, missense_or: null, missense_ci: null,
        case_ptv: null, control_ptv: null, fdr: mdd.fdr });
    } else if (mdd.variant_type === 'missense') {
      rows.push({ id: rowId++, disorder: 'MDD', subtype: 'Missense',
        ptv_pval: null, ptv_qval: null, ptv_or: null, ptv_ci: null,
        missense_pval: mdd.pval, missense_qval: null, missense_or: mdd.or, missense_ci: mdd.or_ci,
        case_ptv: null, control_ptv: null, fdr: mdd.fdr });
    }
  });

  if (associations.ndd) {
    rows.push({ id: rowId++, disorder: 'NDD', subtype: 'De novo',
      ptv_pval: associations.ndd.denovowest_p_full, ptv_qval: null,
      ptv_or: associations.ndd.observed_full && associations.ndd.expected_full
        ? associations.ndd.observed_full / associations.ndd.expected_full : null,
      ptv_ci: null,
      missense_pval: null, missense_qval: null, missense_or: null, missense_ci: null,
      case_ptv: associations.ndd.observed_full ? Math.round(associations.ndd.observed_full) : null,
      control_ptv: associations.ndd.expected_full ? Math.round(associations.ndd.expected_full) : null });
  }

  if (associations.adhd) {
    if (associations.adhd.ptv_pval) {
      rows.push({ id: rowId++, disorder: 'ADHD', subtype: associations.adhd.group_name || 'ADHD',
        ptv_pval: associations.adhd.ptv_pval, ptv_qval: associations.adhd.qval,
        ptv_or: associations.adhd.ptv_or, ptv_ci: associations.adhd.ptv_or_ci,
        missense_pval: null, missense_qval: null, missense_or: null, missense_ci: null,
        case_ptv: associations.adhd.ptv_case_count, control_ptv: associations.adhd.ptv_control_count });
    }
    if (associations.adhd.mis_pval) {
      rows.push({ id: rowId++, disorder: 'ADHD', subtype: associations.adhd.group_name || 'ADHD',
        ptv_pval: null, ptv_qval: null, ptv_or: null, ptv_ci: null,
        missense_pval: associations.adhd.mis_pval, missense_qval: associations.adhd.qval,
        missense_or: associations.adhd.mis_or, missense_ci: associations.adhd.mis_or_ci,
        case_ptv: associations.adhd.mis_case_count, control_ptv: associations.adhd.mis_control_count });
    }
  }

  associations.cognition?.forEach((cog) => {
    rows.push({ id: rowId++, disorder: 'Cognition', subtype: cog.phenotype || '',
      ptv_pval: cog.pval, ptv_qval: cog.qval, ptv_or: null, ptv_ci: null,
      missense_pval: null, missense_qval: null, missense_or: null, missense_ci: null,
      case_ptv: cog.n_carrier, control_ptv: null });
  });

  if (associations.ocd) {
    rows.push({ id: rowId++, disorder: 'OCD', subtype: associations.ocd.group_name || 'OCD+CTD',
      ptv_pval: associations.ocd.tada_pval, ptv_qval: associations.ocd.tada_qval,
      ptv_or: associations.ocd.ptv_or, ptv_ci: associations.ocd.ptv_or_ci,
      missense_pval: null, missense_qval: null, missense_or: null, missense_ci: null,
      case_ptv: associations.ocd.dn_ptv_count, control_ptv: null });
  }

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No association data available for this gene.
      </Typography>
    );
  }

  const columns = [
    { field: 'disorder', headerName: 'Disorder', width: 110,
      renderCell: (p) => {
        const disorderId = disorderIdMap[p.value];
        return (
          <Chip label={p.value} size="small"
            onClick={(e) => { e.stopPropagation(); if (disorderId) navigate(`/browse?disorder=${disorderId}`); }}
            sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }} />
        );
      }
    },
    { field: 'subtype', headerName: 'Subtype', width: 120,
      renderCell: (p) => {
        if (isFilterableSubtype(p.row.disorder, p.value)) {
          const disorderId = disorderIdMap[p.row.disorder];
          return (
            <Chip label={p.value} size="small"
              onClick={(e) => { e.stopPropagation(); navigate(`/browse?disorder=${disorderId}&subtype=${p.value}`); }}
              sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }} />
          );
        }
        return p.value || '';
      }
    },
    { field: 'ptv_pval', headerName: 'PTV P-value', width: 130, type: 'number',
      renderCell: (p) => <PvalChip value={p.value} /> },
    { field: 'ptv_qval', headerName: 'PTV Q-value', width: 130, type: 'number',
      renderCell: (p) => <PvalChip value={p.value} /> },
    { field: 'ptv_or_display', headerName: 'PTV OR (95% CI)', width: 170,
      valueGetter: (value, row) => row.ptv_or,
      renderCell: (p) => formatOR(p.row.ptv_or, p.row.ptv_ci) },
    { field: 'missense_pval', headerName: 'Missense P-value', width: 140, type: 'number',
      renderCell: (p) => <PvalChip value={p.value} /> },
    { field: 'missense_qval', headerName: 'Missense Q-value', width: 140, type: 'number',
      renderCell: (p) => <PvalChip value={p.value} /> },
    { field: 'missense_or_display', headerName: 'Missense OR (95% CI)', width: 170,
      valueGetter: (value, row) => row.missense_or,
      renderCell: (p) => formatOR(p.row.missense_or, p.row.missense_ci) },
    { field: 'case_ptv', headerName: 'Cases PTV', width: 100, type: 'number',
      valueFormatter: (value) => value ?? 'N/A' },
    { field: 'control_ptv', headerName: 'Controls PTV', width: 110, type: 'number',
      valueFormatter: (value) => value ?? 'N/A' },
    { field: 'fdr', headerName: 'FDR', width: 120, type: 'number',
      renderCell: (p) => <PvalChip value={p.value} /> },
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <DataGrid
        rows={rows}
        columns={columns}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
        }}
        pageSizeOptions={[10, 25]}
        disableRowSelectionOnClick
        autoHeight
      />
    </Box>
  );
};

export default AssociationTable;
